import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ScrollText, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/PageHeader";
import { DataTable } from "../../components/DataTable";
import { EmptyState, TableSkeleton } from "../../components/EmptyState";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { api, apiError } from "../../lib/api";
import { formatDateTime, relativeTime } from "../../lib/format";

export default function AdminAudit() {
  const [state, setState] = useState({ items: [], total: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [action, setAction] = useState("");
  const [applied, setApplied] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/admin/audit-logs", {
        params: { page, limit: 25, ...(applied ? { action: applied } : {}) },
      });
      setState(data);
    } catch (error) {
      toast.error(apiError(error, "Could not load the activity log"));
    } finally {
      setLoading(false);
    }
  }, [page, applied]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo(
    () => [
      {
        accessorKey: "createdAt",
        header: "When",
        cell: ({ row }) => (
          <span className="whitespace-nowrap">
            <span className="block text-sm">{formatDateTime(row.original.createdAt)}</span>
            <span className="block text-xs text-muted-foreground">
              {relativeTime(row.original.createdAt)}
            </span>
          </span>
        ),
      },
      {
        accessorKey: "action",
        header: "Action",
        cell: ({ row }) => (
          <span className="inline-flex items-center rounded-full border border-border surface-2 px-2.5 py-0.5 text-xs font-medium">
            {row.original.action}
          </span>
        ),
      },
      {
        accessorKey: "details",
        header: "Details",
        enableSorting: false,
        cell: ({ row }) => <span className="text-sm">{row.original.details || "—"}</span>,
      },
      {
        accessorKey: "adminName",
        header: "Performed by",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">{row.original.adminName}</span>
        ),
      },
    ],
    []
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Activity log"
        subtitle={`${state.total} recorded administrative action${state.total === 1 ? "" : "s"}`}
      />

      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setApplied(action.trim());
        }}
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Filter by action, e.g. client.update"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            data-testid="admin-audit-filter-actor"
          />
        </div>
        <Button type="submit" variant="secondary" data-testid="admin-audit-filter-submit">
          Filter
        </Button>
        {applied ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              setAction("");
              setApplied("");
              setPage(1);
            }}
          >
            Clear
          </Button>
        ) : null}
      </form>

      {loading ? (
        <TableSkeleton cols={4} rows={8} />
      ) : state.items.length === 0 ? (
        <EmptyState
          icon={ScrollText}
          title="No activity recorded"
          description="Every create, update and delete performed in the admin panel is logged here automatically."
          testId="admin-audit-empty-state"
        />
      ) : (
        <DataTable
          columns={columns}
          data={state.items}
          page={page}
          pages={state.pages}
          total={state.total}
          onPageChange={setPage}
          testId="admin-audit-table"
          rowTestId="admin-audit-row"
        />
      )}
    </div>
  );
}
